/**
 * loaderNormalJson_Dynamic.ts - JSON Dynamic Data Support for LoaderNormalJson
 * Applies dynamic data handlers from Global/dynamicdata.json if configured
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { RegistryStore } from '../../../../Assembler/dist/registry/registryStore.js';
import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { JsonArray, JsonObject, JsonValue, JsonValueType } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { NavigationDecision } from '../../../../Assembler/dist/model/index.js';
import { normalizeFileContent, CommonUtil } from '../../../../Assembler/dist/common/commonUtil.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { DynamicConfigMap } from '../../../../Assembler/dist/registry/dynamicConfigMap.js';
import { AppSiteResult } from '../../../../Assembler/dist/registry/iface/appSiteResult.js';
import type { DynamicConfig } from '../../../../Assembler/dist/registry/dynamicConfig.js';
import { buildParentContext } from './loaderNormalJson_Inheritance.js';
import type { LoaderNormalJson } from './loaderNormalJson.js';

type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

/**
 * Gets keys from an object (Map, DynamicConfigMap, JsonObject, or plain object)
 */
function getObjectKeys(obj: unknown): string[] {
    if (!obj) return [];
    if (obj instanceof JsonObject) {
        return Array.from(obj.keys());
    }
    if (obj instanceof Map || obj instanceof DynamicConfigMap) {
        return Array.from((obj as PlainJsonMap).keys());
    }
    if (typeof (obj as { keys?: unknown }).keys === 'function') {
        return ((obj as { keys: () => string[] }).keys());
    }
    return Object.keys(obj as PlainJsonObject);
}

/**
 * Gets a value from an object (Map, DynamicConfigMap, JsonObject, or plain object)
 */
function getObjectValue(obj: unknown, key: string): unknown {
    if (!obj) return undefined;
    if (obj instanceof JsonObject) {
        return obj.get(key);
    }
    if (obj instanceof Map || obj instanceof DynamicConfigMap) {
        return (obj as PlainJsonMap).get(key);
    }
    if (typeof (obj as { get?: unknown }).get === 'function') {
        return (obj as { get: (key: string) => unknown }).get(key);
    }
    return (obj as PlainJsonObject)[key];
}

/**
 * Sets a value in an object (Map, DynamicConfigMap, JsonObject, or plain object)
 */
function setObjectValue(obj: unknown, key: string, value: unknown): void {
    if (!obj) return;
    if (obj instanceof JsonObject) {
        obj.set(key, value);
    } else if (obj instanceof Map || obj instanceof DynamicConfigMap) {
        (obj as PlainJsonMap).set(key, value);
    } else {
        (obj as PlainJsonObject)[key] = value;
    }
}




/**
 * Converts a JsonArray to a plain JavaScript array
 */
function convertJsonArrayToPlain(jsonArray: JsonArray): unknown[] {
    const plainArray: unknown[] = [];
    for (let i = 0; i < jsonArray.length; i++) {
        const item = jsonArray[i];
        if (item instanceof JsonObject) {
            plainArray.push(convertJsonObjectToPlain(item));
        } else if (item instanceof JsonArray) {
            plainArray.push(convertJsonArrayToPlain(item));
        } else {
            plainArray.push(item);
        }
    }
    return plainArray;
}

/**
 * Converts a JsonObject to a plain JavaScript object
 */
function convertJsonObjectToPlain(jsonObject: JsonObject): PlainJsonObject {
    const plainObject: PlainJsonObject = {};
    for (const key of jsonObject.keys()) {
        const value = jsonObject.get(key);
        if (value instanceof JsonObject) {
            plainObject[key] = convertJsonObjectToPlain(value);
        } else if (value instanceof JsonArray) {
            plainObject[key] = convertJsonArrayToPlain(value);
        } else {
            plainObject[key] = value;
        }
    }
    return plainObject;
}

// DynamicView Support


/**
 * Dynamic instance methods to be attached to LoaderNormalJson prototype
 */
export const dynamicMethods = {
    hasDynamicDataHandlers(this: LoaderNormalJson, appSite: string, componentName: string): boolean {
        const config = this.getDynamicDataConfig(appSite);
        if (!config) return false;
        if (config.has('*')) return true;
        const name = componentName.toLowerCase();
        for (const key of config.keys()) if (key.toLowerCase() === name) return true;
        return false;
    },

    hasDynamicActionHandlers(this: LoaderNormalJson, appSite: string, componentName: string): boolean {
        const config = this.getDynamicActionConfig(appSite);
        if (!config) return false;
        if (config.has('*')) return true;
        const name = componentName.toLowerCase();
        for (const key of config.keys()) if (key.toLowerCase() === name) return true;
        return false;
    },

    getTemplateWithView(this: LoaderNormalJson, appSite: string, currentTemplateName: string, componentName: string, appView: string | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false) {
        // clientMode threads into getTemplate → the inheritance seam, where applyClientHandlers consumes the
        // delivered/rendered client-data island. GetTemplateWithView itself only applies view handlers (WS-A).
        const result = this.getTemplate(appSite, currentTemplateName, componentName, appView, flowStack, configJson, clientMode);
        const componentDepth = flowStack?.length ?? 0;
        const htmlJson = this.applyDynamicViewHandlersJson(appSite, appView || '', currentTemplateName, componentName, componentDepth, result.htmlJson, result.dataJson, result.resolvedAppSite, result.resolvedTemplateName, configJson);
        return { ...result, htmlJson };
    },

    tryBuildParentJsonValueHandlerForAction(this: LoaderNormalJson, appSite: string, flowStack: string[] | null, contextJson: JsonObject | null = null): ((key: string) => string | null) | null {
        const parentContext = flowStack && flowStack.length > 0
            ? buildParentContext(flowStack, this._dataJsonCache, appSite, this._dynamicResultsJson.resolvedTemplateJson)
            : null;
        if (!parentContext && !contextJson) {
            return null;
        }
        return (key: string): string | null => {
            const parentValue = parentContext?.get(key.toLowerCase());
            if (parentValue !== undefined && parentValue !== '') {
                return parentValue;
            }
            if (contextJson?.containsKey(key)) {
                // F-SEC1: surface non-string scalars (notably the IsAuthenticated bool) as their string form —
                // asString() returns null for a bool/number, which previously hid IsAuthenticated from the
                // in-handler authz check and forced role-only authz.
                const ctxRaw = contextJson.get(key);
                let ctxValue = ctxRaw.asString();
                if ((ctxValue === null || ctxValue === '') && !ctxRaw.isNull) {
                    ctxValue = ctxRaw.toString();
                }
                if (ctxValue !== null && ctxValue !== '') {
                    return ctxValue;
                }
            }
            return parentValue ?? null;
        };
    },

    applyDynamicDataHandlers(
        this: LoaderNormalJson,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentJson: JsonObject | PlainJsonObject,
        configJson: JsonObject | null = null,
        flowStack: string[] | null = null
    ): void {
        let dynamicDataConfig: DynamicConfigMap | null = this.getDynamicDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicDataConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicDataConfig = this.getDynamicDataConfig(trimmed);
                    if (dynamicDataConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicDataConfig) {
            return;
        }

        let matchedKey: string | null = dynamicDataConfig.findKeyCaseInsensitive(componentName);
        if (!matchedKey && dynamicDataConfig.has('*')) {
            matchedKey = '*';
        }
        if (!matchedKey) {
            return;
        }

        const handlers = dynamicDataConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        for (const handler of handlers) {
            const iDataType: string = handler.iType;
            if (!iDataType) {
                continue;
            }

            if (this.shouldSkipHandler(iDataType, this._skipDynamicData)) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicDataHandlers: Skipping handler '${iDataType}' due to SkipDynamicData='${this._skipDynamicData}'`, 'LoaderNormalJson');
                }
                continue;
            }

            const paramFile: string = handler.iParamFile || '';
            const resultKey = `${componentName}_${iDataType}`;

            if (!RegistryStore.containsData(iDataType)) {
                continue;
            }

            const dataHandler = RegistryStore.getDynamicData(iDataType);
            if (!dataHandler) {
                continue;
            }

            const paramJson: JsonObject = this.getDynamicDataParams(dynamicConfigAppSite, paramFile) ?? new JsonObject();
            const componentJsonObj = componentJson as JsonObject;

            try {
                // Data handlers resolve parent values the SAME way action handlers do: a non-empty inherited
                // parent value wins, otherwise fall back to the server-verified request context, surfacing
                // auth keys (UserId/IsAuthenticated/UserRole/UserAuthMode) to context-aware data providers.
                const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

                const handlerStart = performance.now();
                const resultJson = this.executeWithReadOnly(paramJson, componentJsonObj, configJson, () =>
                    RegistryStore.invokeDynamicData(
                        dataHandler.typeName,
                        appSite,
                        appView,
                        appFile,
                        componentName,
                        componentDepth,
                        resolvedAppSite,
                        resolvedComponentName,
                        paramJson,
                        componentJsonObj,
                        this.getTransientStore(),
                        configJson,
                        getParentJsonValue
                    ) ?? new JsonObject()
                );
                this._dynamicDataNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicDataCount++;

                if (resultJson && typeof resultJson === 'object') {
                    if (this._config!.replaceHandlerReturn) {
                        if (componentJson instanceof Map) {
                            componentJson.clear();
                        } else {
                            for (const key of Object.keys(componentJson as PlainJsonObject)) {
                                delete (componentJson as PlainJsonObject)[key];
                            }
                        }
                    }

                    for (const key of getObjectKeys(resultJson)) {
                        let value = getObjectValue(resultJson, key);
                        if (value instanceof JsonValue) {
                            if (value.isString) value = value.asString();
                            else if (value.isNumber) value = value.asNumber();
                            else if (value.isInteger) value = value.asInteger();
                            else if (value.isBool) value = value.asBool();
                            else if (value.isNull) value = null;
                            else if (value.isArray) value = value.asArray();
                            else if (value.isObject) value = value.asObject();
                        }
                        setObjectValue(componentJson, key, value);
                    }
                }

                // componentJson is read-only during the IData handler call. The loader
                // merges resultJson into componentJson between iterations, so multiple
                // entries for one component alias the latest merged state.
                this._dynamicResultsJson.dynamicDataResults.set(resultKey, componentJson as JsonObject);

                this.syncDynamicActionResults(componentName, componentJson as JsonObject);
            } catch (ex) {
                Logger.error(`ApplyDynamicDataHandlers: Error calling ${iDataType}: ${(ex as Error).message}`, 'LoaderNormalJson');
                // F-ENG3 (handler exception): an IData handler threw, so we log it and surface the exception — the
                // component keeps its un-enriched JSON and the failure is invisible to the response.
                Logger.fallback("F-ENG3",
                    `IData handler threw, component left un-enriched: handler=${iDataType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
    },

    /**
     * Resolves the params for a PURE client handler — one the AppSite declares with DataSource ⊇ "Pure" in
     * dynamicclientdata.json, meaning it produces its output from its declared params alone and never
     * consumes a server-produced raw island.
     *
     * A pure binding names the IClient processor in its IType (e.g. TimeClient), whereas an Embedded binding
     * names the IClientData preparer. That is what lets one config file carry both shapes without ambiguity,
     * and it is why this matches on the handler name off the element rather than on the island key.
     *
     * Returns null when the AppSite declares no pure binding for this component/handler, which is the signal
     * to skip the handler exactly as an absent island always has.
     */
    tryResolvePureClientHandlerParams(this: LoaderNormalJson, appSite: string, componentName: string, clientHandlerName: string): JsonObject | null {
        let dynamicConfig = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return null;
        }

        let matchedKey: string | null = dynamicConfig.findKeyCaseInsensitive(componentName);
        if (!matchedKey && dynamicConfig.has('*')) {
            matchedKey = '*';
        }
        if (!matchedKey) {
            return null;
        }

        for (const handler of dynamicConfig.get(matchedKey) ?? []) {
            if (!handler.hasDataSource('Pure')) continue;
            if (handler.iType.toLowerCase() !== clientHandlerName.toLowerCase()) continue;

            return this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
        }

        return null;
    },

    /**
     * Applies CLIENT-DATA preparer handlers from dynamicclientdata.json for a component. Runs the registered
     * IClientData producer (context-light, via RegistryStore.invokeDynamicClientData) and records the RAW set
     * into dynamicClientDataResults — it never mutates componentJson. The merge records ONLY when Embedded is
     * declared (page-1 raw ships with the render); a Remote-only binding is served on demand by
     * getClientDataSlice (the /api/handler-data endpoint, P3A), never produced at render. configJson is passed
     * as the producer's callerParams.
     */
    applyDynamicClientDataHandlers(this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, configJson: JsonObject | null, flowStack: string[] | null = null): void {
        let dynamicConfig: DynamicConfigMap | null = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return;
        }

        let matchedKey: string | null = dynamicConfig.findKeyCaseInsensitive(componentName);
        if (!matchedKey && dynamicConfig.has('*')) {
            matchedKey = '*';
        }
        if (!matchedKey) {
            return;
        }

        const handlers = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

        for (const handler of handlers) {
            const iClientDataType: string = handler.iType;
            if (!iClientDataType) {
                continue;
            }

            // The merge produces the raw only for an Embedded (or Embedded,Remote hybrid) binding. A Remote-only
            // binding is served on demand by getClientDataSlice (the /api/handler-data endpoint), so skip it here.
            if (!handler.hasDataSource('Embedded')) {
                continue;
            }

            if (!RegistryStore.containsClientData(iClientDataType)) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicClientDataHandlers: IClientData handler '${iClientDataType}' not registered`, 'LoaderNormalJson');
                }
                continue;
            }

            const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();

            try {
                // F2: pin paramJson + componentJson + configJson read-only across the producer — ClientData
                // records the RAW set only and must never mutate the resolved component JSON (invariant I4).
                const handlerStart = performance.now();
                const rawJson = this.executeWithReadOnly(paramJson, componentJson, configJson,
                    () => RegistryStore.invokeDynamicClientData(iClientDataType, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, this.getTransientStore(), configJson, getParentJsonValue) ?? new JsonObject());
                this._dynamicClientDataNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicClientDataCount++;
                this._dynamicResultsJson.dynamicClientDataResults.set(`${componentName}_${iClientDataType}`, JsonValue.object(rawJson));
            } catch (ex) {
                Logger.error(`ApplyDynamicClientDataHandlers: Error calling ${iClientDataType}: ${(ex as Error).message}`, 'LoaderNormalJson');
                // F-ENG3 (handler exception): an IClientData producer threw, so we log it and surface the exception.
                Logger.fallback('F-ENG3', `IClientData handler threw, island not produced: handler=${iClientDataType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
    },

    /**
     * Reconstructs the component's raw template HTML from its HtmlJson structure by concatenating the static
     * ('s') / slot ('x') string chunks in document order. The data-client-handler declarations are literal
     * template markup (no placeholders), so each lives whole inside one static chunk — the joined string is the
     * source CommonUtil.extractClientHandlerDeclarations scans (the Json engines carry no OriginalContent).
     */
    reconstructComponentHtmlFromJson(htmlJson: JsonObject | null): string {
        if (!htmlJson || htmlJson.size === 0) {
            return '';
        }
        let result = '';
        for (const key of htmlJson.keys()) {
            if (key.length > 0 && (key[0] === 's' || key[0] === 'x')) {
                const val = htmlJson.getValue(key);
                if (val && val.isString) {
                    result += val.asString() ?? '';
                }
            }
        }
        return result;
    },

    /**
     * CLIENT-tier data consumption (§2B.5) — NormalJson (structured HtmlJson) mirror of the Normal string
     * engine's applyClientHandlers. For each client-handler declaration on the component template (parsed by
     * CommonUtil.extractClientHandlerDeclarations over the raw HTML reconstructed from the HtmlJson s/x chunks),
     * reads the DELIVERED island raw (_config.dynamicClientData) else the HTML island keyed
     * <ComponentName>_<ClientDataKey>, runs the registered IClient over it (F2 read-only pin), MERGES the
     * filtered output into resolvedJson, re-records the raw into dynamicClientDataResults and the FILTERED into
     * dynamicClientResults. Runs only on the client tier.
     */
    applyClientHandlers(this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, htmlJson: JsonObject | null, resolvedJson: JsonObject, configJson: JsonObject | null, flowStack: string[] | null = null): void {
        const componentHtml = this.reconstructComponentHtmlFromJson(htmlJson);
        if (!componentHtml) {
            return;
        }

        const declarations = CommonUtil.extractClientHandlerDeclarations(componentHtml);
        if (declarations.length === 0) {
            return;
        }

        // Island raw source: the ConfigClient console round-trip carries the delivered map
        // (_config.dynamicClientData); a real browser has no such map and reads the island from the rendered DOM.
        const delivered = this._config!.dynamicClientData;

        const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

        for (const decl of declarations) {
            if (!RegistryStore.containsClientHandler(decl.handlerName)) {
                continue;
            }

            let rawJson: JsonObject;
            let pureParamJson: JsonObject | null = null;
            // The island this declaration CONSUMES. Empty on the pure lane, which consumes none.
            let islandKey = '';
            // The key the consumed OUTPUT is recorded under. Always set, so a pure declaration is still
            // cross-checked by the round-trip: named for the preparer on the island lane (symmetric with
            // dynamicClientDataResults) and for the handler itself on the pure lane, which has no preparer.
            let resultKey = componentName + '_' + decl.handlerName;
            if (!decl.clientDataKey) {
                // PURE lane, DECLARED by the absence of data-client-data: this handler derives its output from
                // its params alone, so no island is looked up at all. The AppSite's dynamicclientdata binding
                // must still declare DataSource "Pure" for it to run, which keeps the JSON declaration the
                // security bound — a component cannot opt itself into island-free execution by editing its HTML.
                pureParamJson = this.tryResolvePureClientHandlerParams(appSite, componentName, decl.handlerName);
                if (pureParamJson === null) {
                    continue;
                }
                rawJson = new JsonObject();
            } else {
                islandKey = componentName + '_' + decl.clientDataKey;
                resultKey = islandKey;
                if (delivered !== null && delivered.has(islandKey) && delivered.get(islandKey).isObject) {
                    rawJson = delivered.get(islandKey).asObject()!;
                } else {
                    const rawStr = CommonUtil.extractClientDataIsland(componentHtml, islandKey);
                    // A DATA-DEPENDENT declaration named a preparer, so a missing island is a MISSING PRODUCER
                    // and the handler is skipped. ⛔ It is deliberately NOT retried on the pure path: the two
                    // lanes are disjoint by declaration now, so a broken producer can no longer be masked by a
                    // pure binding that happens to share the handler name.
                    if (!rawStr) {
                        continue;
                    }
                    rawJson = JsonUtility.parseJsonString(rawStr);
                    if (rawJson.size === 0) {
                        continue;
                    }
                }
            }

            try {
                // A pure handler reads its params from the BINDING (where FixedTime/DateFormat live alongside
                // the server tier's own copy), overlaid with the element's data-client-handler-param so a
                // per-instance override still wins. A data-dependent handler keeps reading the element only.
                let paramJson: JsonObject;
                if (pureParamJson === null) {
                    paramJson = JsonUtility.parseJsonString(decl.param) ?? new JsonObject();
                } else {
                    paramJson = pureParamJson;
                    const declOverride = JsonUtility.parseJsonString(decl.param);
                    if (declOverride !== null) {
                        for (const [k, v] of declOverride.entries()) {
                            paramJson.set(k, v);
                        }
                    }
                }
                // F2: pin the delivered island raw + paramJson read-only across the IClient invocation.
                const handlerStart = performance.now();
                const filtered = this.executeWithReadOnly(paramJson, rawJson, null,
                    () => RegistryStore.invokeDynamicClient(decl.handlerName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, resolvedJson, this.getTransientStore(), configJson, getParentJsonValue, rawJson) ?? new JsonObject());
                this._dynamicClientDataNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicClientDataCount++;
                for (const [k, v] of filtered.entries()) {
                    resolvedJson.set(k, v);
                }
                // Re-emit the delivered island at the page tail, matching the server byte-for-byte. A PURE
                // handler consumed no island — rawJson is the empty stand-in built above — so there is nothing
                // to re-emit; emitting it would append an island the server never produced.
                if (pureParamJson === null) {
                    this._dynamicResultsJson.dynamicClientDataResults.set(islandKey, JsonValue.object(rawJson));
                }
                // Record the CONSUMED (filtered) output — the client mirror of the server DynamicData.
                // Keyed by resultKey, NOT islandKey: a pure declaration has no island key, and recording it
                // under an empty key would collide every pure handler in the component onto one slot.
                this._dynamicResultsJson.dynamicClientResults.set(resultKey, JsonValue.object(filtered));
            } catch (ex) {
                Logger.error(`ApplyClientHandlers: Error running ${decl.handlerName}: ${(ex as Error).message}`, 'LoaderNormalJson');
                // F-ENG3 (handler exception): an IClient consumer threw, so we log it and surface the exception.
                Logger.fallback('F-ENG3', `IClient handler threw, component left un-enriched: handler=${decl.handlerName} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
    },

    /**
     * P3A ON-DEMAND slice for the /api/handler-data endpoint. Resolves the client-data binding for handlerName
     * and returns the raw producer output via the SAME context-light producer the merge uses
     * (RegistryStore.invokeDynamicClientData) — one producer, two seams. Does NOT run a page merge; callerParams
     * is handed straight in as paging keys. Returns null when the AppSite declares no binding for the handler
     * (endpoint → 400, no arbitrary handler probing).
     *
     * Not gated on DataSource ⊇ Remote: that flag says WHERE the island is delivered at render time (Embedded =
     * with the server's own render), not who is allowed to ask for it. A client rendering the AppSite itself
     * needs exactly the data the Embedded island would have carried, so gating here would force every Embedded
     * AppSite to redeclare itself merely to be client-mergeable. The declaration in dynamicclientdata.json
     * remains the bound: an undeclared handler is still unreachable.
     */
    getClientDataSlice(this: LoaderNormalJson, appSite: string, handlerName: string, callerParams: JsonObject): JsonObject | null {
        if (!handlerName) {
            return null;
        }

        let dynamicConfig: DynamicConfigMap | null = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return null;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (!handler.iType || handler.iType.toLowerCase() !== handlerName.toLowerCase()) {
                    continue;
                }
                if (!RegistryStore.containsClientData(handlerName)) {
                    return null;
                }

                const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
                // Remote/on-demand endpoint: no merge context, so the merge-only params are passed empty and the
                // per-request config rides in as configJson (the producer reads only appSite/component/paramJson).
                return RegistryStore.invokeDynamicClientData(handlerName, appSite, '', '', boundComponentName, 0, appSite, boundComponentName, paramJson, new JsonObject(), this.getTransientStore(), callerParams, null) ?? new JsonObject();
            }
        }

        return null;
    },

    /**
     * Produces every declared client-data island for the AppSite, keyed "<component>_<IType>" — the island set a
     * client merge of this AppSite needs when the page carries none of its own (see ILoader.getClientDataIslands).
     * Runs the same context-light producer as the merge and the single-slice endpoint, over every binding
     * dynamicclientdata.json declares, so the set is byte-identical to the islands a server render of this
     * AppSite would have embedded.
     */
    getClientDataIslands(this: LoaderNormalJson, appSite: string, callerParams: JsonObject): JsonObject {
        const islands = new JsonObject();

        let dynamicConfig: DynamicConfigMap | null = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return islands;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (!handler.iType || !RegistryStore.containsClientData(handler.iType)) {
                    continue;
                }

                const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
                // Same shape as the merge's applyDynamicClientDataHandlers record: key by the BOUND component,
                // because that is the component the consumer's island lookup derives (component + "_" + key).
                const raw = RegistryStore.invokeDynamicClientData(handler.iType, appSite, '', '', boundComponentName, 0, appSite, boundComponentName, paramJson, new JsonObject(), this.getTransientStore(), callerParams, null);
                if (raw) {
                    islands.set(boundComponentName + '_' + handler.iType, JsonValue.object(raw));
                }
            }
        }

        return islands;
    },

    /**
     * True when the AppSite declares a client-data binding with a registered producer (see ILoader.hasClientData).
     * Same predicate as getClientDataIslands, declaration lookup only.
     */
    hasClientData(this: LoaderNormalJson, appSite: string): boolean {
        let dynamicConfig: DynamicConfigMap | null = this.getDynamicClientDataConfig(appSite);
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return false;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (handler.iType && RegistryStore.containsClientData(handler.iType)) {
                    return true;
                }
            }
        }

        return false;
    },

    applyDynamicViewHandlersJson(
        this: LoaderNormalJson,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        componentHtmlJson: JsonObject,
        componentJson: JsonObject | null,
        resolvedAppSite: string,
        resolvedComponentName: string,
        configJson: JsonObject | null = null
    ): JsonObject {
        if (componentHtmlJson.size === 0) return componentHtmlJson;
        if (componentName.startsWith('!')) return componentHtmlJson;
        if (resolvedComponentName === this._config!.defaultSkipTemplateName || resolvedComponentName === this._config!.defaultReplaceTemplateName) return componentHtmlJson;
        return this._applyDynamicViewHandlersJson(appSite, appView, appFile, componentName, componentDepth, componentHtmlJson, componentJson, resolvedAppSite, resolvedComponentName, configJson);
    },

    _applyDynamicViewHandlersJson(
        this: LoaderNormalJson,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        componentHtmlJson: JsonObject,
        componentJson: JsonObject | null,
        resolvedAppSite: string,
        resolvedComponentName: string,
        configJson: JsonObject | null = null
    ): JsonObject {
        let dynamicConfig: DynamicConfigMap | null = this.getDynamicViewConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicViewConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return componentHtmlJson;
        }

        const keysToProcess: string[] = [];
        if (dynamicConfig.has('*')) {
            keysToProcess.push('*');
        }

        let foundSpecificMatch = false;
        const matchedKey = dynamicConfig.findKeyCaseInsensitive(componentName);
        if (matchedKey && matchedKey !== '*') {
            keysToProcess.push(matchedKey);
            foundSpecificMatch = true;
        }

        if (!foundSpecificMatch) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    const searchConfig = this.getDynamicViewConfig(trimmed);
                    if (!searchConfig) continue;

                    const searchMatchedKey = searchConfig.findKeyCaseInsensitive(componentName);
                    if (searchMatchedKey && searchMatchedKey !== '*') {
                        keysToProcess.push(searchMatchedKey);
                        dynamicConfig = searchConfig;
                        dynamicConfigAppSite = trimmed;
                        foundSpecificMatch = true;
                        break;
                    }

                    if (!keysToProcess.includes('*') && searchConfig.has('*')) {
                        keysToProcess.unshift('*');
                        dynamicConfig = searchConfig;
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }

        if (keysToProcess.length === 0) {
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                const availableKeys = Array.from(dynamicConfig.keys());
                Logger.debug(`ApplyDynamicViewHandlersJson: No handlers for componentName='${componentName}', available keys: ${availableKeys.join(', ')}`, 'LoaderNormalJson');
            }
            return componentHtmlJson;
        }

        let resultHtmlJson = componentHtmlJson;
        for (const dynamicKey of keysToProcess) {
            const handlers = dynamicConfig.get(dynamicKey);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`ApplyDynamicViewHandlersJson: Found ${handlers.length} handlers for ${componentName} (key: ${dynamicKey})`, 'LoaderNormalJson');
            }

            for (const handler of handlers) {
                const iViewType: string = handler.iType;
                if (!iViewType) {
                    continue;
                }
                if (this.shouldSkipHandler(iViewType, this._skipDynamicView)) {
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`ApplyDynamicViewHandlersJson: Skipping handler '${iViewType}' due to SkipDynamicView='${this._skipDynamicView}'`, 'LoaderNormalJson');
                    }
                    continue;
                }

                const paramFile: string = handler.iParamFile || '';
                if (!RegistryStore.containsView(iViewType)) {
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`ApplyDynamicViewHandlersJson: IView handler '${iViewType}' not registered`, 'LoaderNormalJson');
                    }
                    continue;
                }

                const viewHandler = RegistryStore.getDynamicView(iViewType);
                if (!viewHandler) {
                    continue;
                }

                const params = this.getDynamicViewParams(dynamicConfigAppSite, paramFile);
                const paramJson = new JsonObject();
                if (params) {
                    const paramsKeys = params instanceof Map ? Array.from(params.keys()) : Object.keys(params as PlainJsonObject);
                    for (const key of paramsKeys) {
                        const value = params instanceof Map ? params.get(key) : (params as PlainJsonObject)[key];
                        paramJson.set(key, value);
                    }
                }

                let compJson: JsonObject;
                if (componentJson instanceof JsonObject) {
                    compJson = componentJson;
                } else if (componentJson && typeof componentJson === 'object' && !((componentJson as unknown) instanceof JsonArray)) {
                    compJson = new JsonObject();
                    for (const key of getObjectKeys(componentJson)) {
                        compJson.set(key, getObjectValue(componentJson, key));
                    }
                } else {
                    compJson = new JsonObject();
                }

                // Dispatch through the registry, which owns the IView/IViewJson decision and the
                // null-argument guards. A null return means the registered handler is HTML-only,
                // so this JSON engine has nothing to apply and the component keeps its HtmlJson.
                const handlerStart = performance.now();
                const jsonResult = this.executeWithReadOnly(
                    paramJson,
                    compJson,
                    configJson,
                    () => RegistryStore.invokeDynamicViewJson(viewHandler.typeName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, resultHtmlJson, paramJson, compJson, configJson)
                );
                if (jsonResult !== null) {
                    this._dynamicViewNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                    this._dynamicViewCount++;
                    resultHtmlJson = jsonResult;
                }

                const resultKey = `${componentName}_${iViewType}`;
                const resultHtml = normalizeFileContent(GenerateHtmlJson.convertJsonToHtml(resultHtmlJson));
                this._dynamicResultsJson.dynamicViewResults.set(resultKey, resultHtml);
            }
        }

        return resultHtmlJson;
    },

    applyDynamicActionHandlers(
        this: LoaderNormalJson,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentJson: JsonObject | PlainJsonObject,
        configJson: JsonObject | null = null,
        flowStack: string[] | null = null
    ): void {
        if (!this._actionMode) {
            return;
        }

        void componentDepth;

        let dynamicConfig: DynamicConfigMap | null = this.getDynamicActionConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicActionConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return;
        }

        let matchedKey: string | null = dynamicConfig.findKeyCaseInsensitive(componentName);
        if (!matchedKey && dynamicConfig.has('*')) {
            matchedKey = '*';
        }
        if (!matchedKey) {
            return;
        }

        const handlers = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        const actionJson = configJson;
        for (const handler of handlers) {
            const iActionType: string = handler.iType;
            if (!iActionType) continue;

            const paramFile: string = handler.iParamFile || '';
            if (!RegistryStore.containsAction(iActionType)) continue;

            const actionHandler = RegistryStore.getDynamicAction(iActionType);
            if (!actionHandler) continue;

            const paramJson = this.getDynamicActionParams(dynamicConfigAppSite, paramFile) ?? new JsonObject();
            const resultKey = `${componentName}_${iActionType}`;

            try {
                // Parent-value lookup (parent-context snapshot + request context fallback), shared with data handlers.
                const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

                const componentJsonObj = componentJson as JsonObject;
                const handlerStart = performance.now();
                this.executeDynamicAction(appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, paramJson, componentJsonObj, actionJson, actionHandler, getParentJsonValue);
                this._dynamicActionNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicActionCount++;

                // componentJson is read-only during DoAction (executeWithReadOnly) and
                // current IAction handlers communicate via transientStore, never mutating
                // componentJson. syncDynamicActionResults overwrites this entry after
                // any later data merge, so storing the reference here is safe.
                this._dynamicResultsJson.dynamicActionResults.set(resultKey, componentJsonObj);
            } catch (ex) {
                Logger.error(`ApplyDynamicActionHandlers: Error calling ${iActionType}: ${(ex as Error).message}`, 'LoaderNormalJson');
            }
        }
    },

    executeDynamicAction(this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionHandler: NonNullable<ReturnType<typeof RegistryStore.getDynamicAction>>, getParentJsonValue: ((key: string) => string | null) | null): void {
        this.executeWithReadOnly(
            paramJson,
            componentJson,
            actionJson,
            () => RegistryStore.invokeDynamicAction(actionHandler.typeName, appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, paramJson, componentJson, this.getTransientStore(), actionJson, getParentJsonValue)
        );
    },

    executeWithReadOnly<T>(this: LoaderNormalJson, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, action: () => T): T {
        const paramWasReadOnly = paramJson.isReadOnly;
        const componentWasReadOnly = componentJson.isReadOnly;
        const configWasReadOnly = configJson ? configJson.isReadOnly : false;

        paramJson.isReadOnly = true;
        componentJson.isReadOnly = true;
        if (configJson) {
            configJson.isReadOnly = true;
        }

        try {
            return action();
        } finally {
            paramJson.isReadOnly = paramWasReadOnly;
            componentJson.isReadOnly = componentWasReadOnly;
            if (configJson) {
                configJson.isReadOnly = configWasReadOnly;
            }
        }
    },

    syncDynamicActionResults(this: LoaderNormalJson, componentName: string, afterJson: unknown): void {
        if (!this._actionMode || afterJson === null || afterJson === undefined) {
            return;
        }

        const prefix = `${componentName}_`.toLowerCase();
        for (const key of Array.from(this._dynamicResultsJson.dynamicActionResults.keys())) {
            if (key.toLowerCase().startsWith(prefix)) {
                this._dynamicResultsJson.dynamicActionResults.set(key, afterJson as never);
            }
        }
    },

    _applyDynamicDataHandlersSync(this: LoaderNormalJson, appSite: string, componentName: string, componentJson: PlainJsonObject): void {
        const dynamicConfig = this.getDynamicDataConfig(appSite);
        if (!dynamicConfig) {
            return;
        }

        const matchedKey = dynamicConfig.findKeyCaseInsensitive(componentName);
        if (!matchedKey) {
            return;
        }

        const handlers = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        for (const handlerObj of handlers) {
            const iDataType = handlerObj.iType;
            if (!iDataType) {
                continue;
            }

            const paramFile = handlerObj.iParamFile || '';
            const resultKey = `${componentName}_${iDataType}`;
            if (this._dynamicResultsJson.dynamicDataResults && this._dynamicResultsJson.dynamicDataResults.has(resultKey)) {
                const cachedData = this._dynamicResultsJson.dynamicDataResults.get(resultKey).asObject();
                if (cachedData) {
                    for (const [key, value] of cachedData) {
                        componentJson[key] = value;
                    }
                }
                continue;
            }

            if (!RegistryStore.containsData(iDataType)) {
                continue;
            }

            const dataHandler = RegistryStore.getDynamicData(iDataType);
            if (!dataHandler) {
                continue;
            }

            const params = this.getDynamicDataParams(appSite, paramFile);
            const paramJsonObj = params ?? new JsonObject();
            const wrapperJsonObj = new JsonObject(componentJson || {});

            try {
                const resultJson = this.executeWithReadOnly(paramJsonObj, wrapperJsonObj, null, () =>
                    RegistryStore.invokeDynamicData(dataHandler.typeName, appSite, '', '', componentName, 0, appSite, componentName, paramJsonObj, wrapperJsonObj, this.getTransientStore(), null) ?? new JsonObject());
                if (resultJson) {
                    for (const [key, _value] of resultJson) {
                        componentJson[key] = resultJson.get(key);
                    }
                }
            } catch (ex) {
                Logger.error(`ApplyDynamicDataHandlers: Error calling ${iDataType}: ${(ex as Error).message}`, 'LoaderNormalJson');
                // F-ENG3 (handler exception): an IData handler threw, so we log it and surface the exception — the
                // component keeps its un-enriched JSON and the failure is invisible to the response.
                Logger.fallback("F-ENG3",
                    `IData handler threw, component left un-enriched: handler=${iDataType} component=${componentName} appSite=${appSite} appView= ex=${(ex as Error).message}`);
                throw ex;
            }

            for (const key of Object.keys(componentJson)) {
                const value = componentJson[key];
                if (value instanceof JsonArray) {
                    componentJson[key] = this._convertJsonArrayToPlain(value);
                } else if (value instanceof JsonObject) {
                    componentJson[key] = this._convertJsonObjectToPlain(value);
                }
            }
        }
    },

    _convertJsonArrayToPlain(this: LoaderNormalJson, jsonArray: JsonArray): unknown[] {
        const plainArray: unknown[] = [];
        for (let i = 0; i < jsonArray.length; i++) {
            let item: unknown = jsonArray[i];
            if (item instanceof JsonValue) {
                if (item.isObject) {
                    item = item.asObject();
                } else if (item.isArray) {
                    item = item.asArray();
                } else {
                    plainArray.push(this._unwrapJsonValue(item));
                    continue;
                }
            }
            if (item instanceof JsonObject) {
                plainArray.push(this._convertJsonObjectToPlain(item));
            } else if (item instanceof JsonArray) {
                plainArray.push(this._convertJsonArrayToPlain(item));
            } else {
                plainArray.push(item);
            }
        }
        return plainArray;
    },

    _convertJsonObjectToPlain(this: LoaderNormalJson, jsonObject: JsonObject): PlainJsonObject {
        const plainObject: PlainJsonObject = {};
        for (const key of jsonObject.keys()) {
            let value: unknown = jsonObject.get(key);
            if (value instanceof JsonValue) {
                if (value.isObject) {
                    value = value.asObject();
                } else if (value.isArray) {
                    value = value.asArray();
                } else {
                    plainObject[key] = this._unwrapJsonValue(value);
                    continue;
                }
            }
            if (value instanceof JsonObject) {
                plainObject[key] = this._convertJsonObjectToPlain(value);
            } else if (value instanceof JsonArray) {
                plainObject[key] = this._convertJsonArrayToPlain(value);
            } else {
                plainObject[key] = value;
            }
        }
        return plainObject;
    },

    _unwrapJsonValue(this: LoaderNormalJson, jsonValue: JsonValue): unknown {
        switch (jsonValue.typeName) {
            case JsonValueType.String:
                return jsonValue.asString();
            case JsonValueType.Number:
                return jsonValue.asNumber();
            case JsonValueType.Integer:
                return jsonValue.asInteger();
            case JsonValueType.Bool:
                return jsonValue.asBool();
            case JsonValueType.Array:
                return this._convertJsonArrayToPlain(jsonValue.asArray()!);
            case JsonValueType.Object:
                return this._convertJsonObjectToPlain(jsonValue.asObject()!);
            case JsonValueType.Null:
                return null;
            default:
                return null;
        }
    },

    /**
     * Resolves the AppSite/AppView to render by running the IAppSite handlers configured for the
     * requested AppSite (dynamicappsite.json, keyed by AppSite name). AppSite-specific handlers run
     * first, then the wildcard ("*") handlers LAST. Dormant infrastructure until IAppSite handlers are
     * registered and the merge pipeline calls it (later phase).
     */
    applyDynamicAppSiteHandlers(this: LoaderNormalJson, appSite: string, appView: string | null, contextJson: JsonObject, configJson: JsonObject | null): AppSiteResult {
        this._requestContextJson = contextJson;
        // Reset the per-merge navigation trace; one decision is appended per handler invocation below.
        this._lastNavigationTrace = [];

        let curAppSite = appSite;
        let curAppView = appView ?? '';

        const appSiteConfig = this.getDynamicAppSiteConfig(appSite);
        if (!appSiteConfig) return new AppSiteResult(curAppSite, curAppView);

        const specificHandlers = appSiteConfig.get(appSite);
        const wildcardHandlers = appSite === '*' ? null : appSiteConfig.get('*');
        const handlers: DynamicConfig[] = [];
        if (specificHandlers && Array.isArray(specificHandlers)) handlers.push(...specificHandlers);
        if (wildcardHandlers && Array.isArray(wildcardHandlers)) handlers.push(...wildcardHandlers);
        if (handlers.length === 0) return new AppSiteResult(curAppSite, curAppView);

        for (const handler of handlers) {
            const iAppSiteType = handler.iType;
            if (!iAppSiteType) {
                continue;
            }

            const provider = RegistryStore.getDynamicAppSite(iAppSiteType);
            if (!provider) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicAppSiteHandlers: IAppSite handler '${iAppSiteType}' not registered`, 'LoaderNormalJson');
                }
                continue;
            }

            const paramJson = this.getDynamicAppSiteParams(appSite, handler.iParamFile || '') ?? new JsonObject();
            try {
                const resolved = RegistryStore.invokeDynamicAppSite(provider.typeName, curAppSite, curAppView, paramJson, contextJson, configJson);
                if (resolved) {
                    const changed = curAppSite !== resolved.appSite || curAppView !== resolved.appView;
                    if (changed) {
                        Logger.flow(`ApplyDynamicAppSiteHandlers: redefined ${curAppSite}/${curAppView} -> ${resolved.appSite}/${resolved.appView} via ${iAppSiteType}`, 'NormalJson.MergeLifecycle');
                    }
                    // Preserve every hop (change or no-op) so a scenario can assert the path, not only the endpoint.
                    this._lastNavigationTrace.push(new NavigationDecision(
                        iAppSiteType, curAppSite, curAppView, resolved.appSite, resolved.appView, changed, resolved.reason));
                    curAppSite = resolved.appSite;
                    curAppView = resolved.appView;
                }
            } catch (ex) {
                // F-SEC5 (hardened, fail-closed): an AppSite handler computes the authoritative auth redirect
                // (e.g. unauth Admin -> Login). If it throws, keeping curAppSite/curAppView at the requested
                // (protected) values would render the PROTECTED page — a fail-OPEN. Surface the failure instead.
                Logger.error(`ApplyDynamicAppSiteHandlers: Error calling ${iAppSiteType}: ${(ex as Error).message}`, 'LoaderNormalJson');
                Logger.fallback("F-SEC5",
                    `AppSite handler ${iAppSiteType} threw; failing closed (would have kept protected appSite=${curAppSite}/${curAppView}): ${(ex as Error).message}`);
                throw ex;
            }
        }

        if (curAppSite !== appSite) {
            this.ensureAppSiteLoaded(curAppSite);
        }

        return new AppSiteResult(curAppSite, curAppView);
    },
};


